import { AODA } from "@core/utilities/aoda";
import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from "@angular/core";
import { BaseComponent } from "@shared/components/base.component";
import { PageEvent } from "@angular/material/paginator";
import { FormGroup } from "@angular/forms";
import { Search } from "./search.model";
import { HttpClient } from "@angular/common/http";
import { environment } from '@environments/environment';
import { ElasticSearchQueryBody } from '../shared/models/common/elasticsearch-query-body.model';
import { Constants } from "@shared/models/common/constants";
import { forkJoin, Observable, takeWhile } from "rxjs";
import { LoadingService } from "@shared/services/loading.service";
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';  
import { CommaSeparatedPipe } from "@shared/pipes/comma-separated,pipe";
import { DatabaseIndexNameType } from "@shared/models/common/database-index-name.model";
import { ElasticsearchQueryService } from "@shared/services/elasticsearch-query.service";
import { DatabaseType } from "@shared/models/common/database-type.model";
import * as moment from "moment";
import { MegabyteConversionPipe } from "@shared/pipes/megabyte.conversion.pipe";

@Component({
  selector: "app-search",
  templateUrl: "./search.component.html",
  styleUrls: ["./search.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  f: FormGroup;
  searchInput: string;
  results: any[] = [];
  clearFilter: boolean = false;
  searchPressed: boolean = false;
  databaseIsSelected: boolean = true;
  totalResults = 0;
  minimumShouldMatch = 0;
  searchTerms: string;
  indexes: string;

  //Auto-complete and suggestions
  corrections: any[] = [];
  autoCompleteSuggestions: any[] = [];
  autoCompleteOptionSelected: boolean = false;
  
  // Overlay spinner configuration
  color = "accent";
  mode = "indeterminate";
  value = 50;
  displayProgressSpinner = false;
  spinnerWithoutBackdrop = false;

  // Filter Variables
  enableFilter: boolean = true;
  dateFilterEnabled: boolean = false;
  commodityFilterEnabled: boolean = false;

  // Pagination Variables
  displayPagination: boolean = false;
  startIndex: number = 0;
  endIndex: number = 0;
  pageIndex: number = 0;
  resultsPerPage: number = 10;
  visitedResults: any[] = [];
  viewingLastPage: boolean = false;
  visitedLastPageFirst = false;

  // Export Spreadsheet Variables
  mineral_inventory_arr: any[] = [];
  assessment_arr: any[] = [];
  drill_hole_arr: any[] = [];
  publication_arr: any[] = [];
  abandoned_mine_arr: any[] = [];
  assessment_pdf_arr: any[] = [];
  publication_pdf_arr: any[] = [];

  //Fix for #92892
  specialCharacters: string[] = [
    '!', '@', '#', '$', '%', '^', '&', '*', '-', '–', '_', '=', '+',
    '[', ']', '{', '}', '\\', '|', ';', ':', '\'', '"', '<', '>', '/',
    '?', '`', '~', '®', '©', '°', '™', '±'
  ];

  constructor(
    private injector: Injector,
    private el: ElementRef,
    private r2: Renderer2,
    private http: HttpClient,
    private loadingService: LoadingService,
    private commaSeparatedPipe: CommaSeparatedPipe,
    private megabyteConversionPipe: MegabyteConversionPipe,
    private elasticSearchQueryService: ElasticsearchQueryService
  ) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();
    this.loadSearchForm();
    this.connectPageListeners();

    if (!this.f) {
      this.initializeParameters();
      this.bindForm();
    }

    this.indexes = this.fetchIndexes();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.searchResultsCache.setForm(this.f);
    this.searchResultsCache.setResults(this.results);
  }

  ngAfterViewInit(): void {
    AODA.applyAllFixes(this.el, this.r2, this.languageService.languageType);
  }

  bindForm() {
    this.f = new Search().toForm();
  }

  initializeParameters() {
    this.results = [];
    this.visitedResults = [];
    this.totalResults = 0;
    this.startIndex = 0;
    this.endIndex = 0;
    this.pageIndex = 0;
    this.minimumShouldMatch = 0;
    this.viewingLastPage = false;
    this.displayPagination = false;
    this.clearFilter = false;
    this.searchPressed = false;
    this.corrections = [];
    this.autoCompleteSuggestions = [];
  }

  loadSearchForm() {
    this.f = this.searchResultsCache.getForm();
    this.results = this.searchResultsCache.getResults();
    this.visitedResults = this.searchResultsCache.getVisitedResults();
    this.totalResults = this.searchResultsCache.getTotalResults();
    this.startIndex = this.searchResultsCache.getStartIndex();
    this.endIndex = this.searchResultsCache.getEndIndex();
    this.pageIndex = this.searchResultsCache.getPageIndex();
    this.resultsPerPage = this.searchResultsCache.getResultsPerPage();
    this.visitedLastPageFirst =
      this.searchResultsCache.getVisitedLastPageFirst();

    if (this.f) {
      this.searchInput = this.f.controls["searchValue"].value;
    }

    if (this.results && this.results.length > 0) {
      this.isViewingLastPage();
      this.displayPagination = true;
      this.searchPressed = true;
    }
  }

  
  // ElasticSearch Functions
  // Helper function to construct the ElasticSearch query body
  constructESQueryStructure(elasticSearchQueryBody: ElasticSearchQueryBody) {
    var queryObj = {};
    var boolObj = {};
    var shouldArr = [];

    if (!("query" in elasticSearchQueryBody)) {
      queryObj = elasticSearchQueryBody["query"] = {};
    }

    if (!("bool" in elasticSearchQueryBody)) {
      boolObj = queryObj["bool"] = { minimum_should_match: 1 };
    }

    if (!("should" in elasticSearchQueryBody)) {
      shouldArr = boolObj["should"] = [];
    }

    // Reset minimum match count
    this.minimumShouldMatch = 0;

    if (this.hasSearchTerm()) {
      /*
        - ElasticSearch multi-match
        - Variable fuzziness, varies based on the length of the search term
        - All terms must exist, helps with returning more relevant search results, especially for auto-complete
      */
      const regex = new RegExp(`[${this.specialCharacters.map(ch => '\\' + ch).join('')}]`, 'g');
      var sanitzedInput = this.searchInput.replace(regex, ' ');

      shouldArr.push({
        multi_match: {
          query: sanitzedInput,
          // query: this.formatSearchInput(this.searchInput),
          // fuzziness: "auto",
          operator: "and",
          type: "bool_prefix",
          fields: [
            "abandoned_mine_identifier^10",
            "features.int_featureautoid^10",
            "file_id^10",
            "mdi_ident^10",
            "publ_num^10",
            "features.feature_type^8",
            "official_name^8",
            "authors.company^8",
            "authors.name^8",
            "file_identifiers.id^8",
            "performed_for.name^8",
            "property_names.name^8",
            "company_name^8",
            "property_name^8",
            "tech_id^8",
            "all_names.md_name^8",
            "title^8",
            "meta.title^8",
            "contributors.contributor^8",
            "primary_commodity^7",
            "alt_name.alternate_name^7",
            "commodities.description^7",
            "work_types.comments^7",
            "work_types.description^7",
            "elements.name^7",
            "lithology.rock_type.deposit_rock_name^7",
            "primary_commodities.description^7",
            "production.commodities.description^7",
            "reserves.commodities.commodity^7",
            "secondary_commodities.description^7",
            "mineralization.mineral_name.name^7",
            "keywords^7",
            "division.description^6",
            "mndm_township_area_name^6",
            "nts_number^6",
            "nts.nts_num^6",
            "primary_township^6",
            "resident_geologist_district^6",
            "secondary_townships.name^6",
            "company_hole_id^6",
            "id^6",
            "nts_num^6",
            "rgp_district^6",
            "township^6",
            "classifications.classification.deposit_classification^6",
            "deposit_status^6",
            "nts.nts_quadrangle^6",
            "rgp_district^6",
            "townships.name^6",
            "references.publ_num^6",
            "nts.area^6",
            "rgp_district.name^6",
            "associated_publications.publ_num^6",
            "go_file.name^6",
            "abstract^6",
            "purpose^6",
            "associated_publications.title^5",
            "features.feature_class^4",
            "jurisdiction^4",
            "mdi_identifier_new^4",
            "mine_site_class^4",
            "status^4",
            "claim_holders.claim_holder_name^4",
            "technical_reports.comments^4",
            "technical_reports.file_name^4",
            "technical_reports.title^4",
            "drill_core.name^4",
            "amis_id^4",
            "assessment_work.file_id^4",
            "belt^4",
            "characteristics.characteristic.deposit_characteristic^4",
            "domain^4",
            "exploration_history^4",
            "formation_group^4",
            "intrusion_name^4",
            "province^4",
            "references.title^4",
            "related_records.related_deposit_mdi_ident^4",
            "reserves.category.resourcemd_category^4",
            "reserves.ore_zone^4",
            "subprovince^4",
            "supergroup^4",
            "tectonic_assemblage^4",
            "terrane^4",
            "type^4",
            "series_en^4",
            "series_fr^4",
            "topics.name^4",
            "closure_reason^3",
            // "date_mineclosed^3",
            // "date_mineopened^3",
            "claim_holders.ministry_client_num^3",
            "claims.clm_cell_num^3",
            "work_types.group_name^3",
            // "year_from^3",
            // "year_to^3",
            // "year_drilled^3",
            // "all_names.year^3",
            "mineralization.alteration_mineral_types.alteration_mineral_type^3",
            "mineralization.alteration_style.name^3",
            "mineralization.class^3",
            "mineralization.economic_mineral_type^3",
            "production.comments^3",
            // "production.year_from^3",
            // "production.year_to^3",
            // "reserves.year^3",
            "site_visits.visit_comment^3",
            // "year^3",
            // "release_date^3",
            "amis_district_description^2",
            "features.hazard_status^2",
            "features.mine_feature_condition_desc^2",
            "operational_access^2",
            "file_type^2",
            "comments^2",
            "dh_type^2",
            "elements.description^2",
            "assay_sample.analytical_method.name^2",
            "assay_sample.commodity.description^2",
            "assay_sample.digestion_method.name^2",
            "lithology.composition^2",
            "lithology.texture^2",
            "metamorphism_type^2",
            "mineral_zones.name^2",
            "references.originator^2",
            "references.publisher^2",
            "reserves.reference^2",
            "site_visits.geologist.first_name^2",
            "site_visits.geologist.last_name^2",
            "assessment_work.rgp_file_nr^2",
            "reserves.comments^2",
            "closure_plan^1",
            "concession_number^1",
            "features.feature_depth_or_height^1",
            "features.feature_length^1",
            "features.feature_width^1",
            "lot_number^1",
            "mdi_identifier_old^1",
            "mine_plan_or_section^1",
            "progressive_rehab_plan^1",
            "commodities.category^1",
            "file_identifiers.name^1",
            "maps.comments^1",
            "maps.doc_type^1",
            "maps.file_name^1",
            "maps.map_type^1",
            "maps.scale^1",
            "maps.title^1",
            // "technical_reports.year^1",
            // "total_value_of_work_approved^1",
            "work_types.quantity^1",
            "azimuth^1",
            "coordinate_source^1",
            "dip^1",
            "length^1",
            "overburden^1",
            "assay_sample.limit^1",
            "assay_sample.qualifier^1",
            "assay_sample.result^1",
            "assay_sample.unit^1",
            "assessment_work.url^1",
            "comments.geology_comment^1",
            "comments.subject_type^1",
            "deposit_access_description^1",
            "geochronological_age^1",
            "geochronological_age_ref^1",
            "geological_age^1",
            "geologist.comments.first_name^1",
            "geologist.comments.last_name^1",
            "lithology.relationship^1",
            "location_method^1",
            "metamorphism_grade^1",
            "mineral_zones.age^1",
            // "mineral_zones.depth^1",
            // "mineral_zones.dip^1",
            "mineral_zones.id^1",
            // "mineral_zones.length^1",
            // "mineral_zones.plunge^1",
            "mineral_zones.reference^1",
            "mineral_zones.shape.name^1",
            // "mineral_zones.strike^1",
            // "mineral_zones.thickness^1",
            "mineral_zones.trend^1",
            "mineral_zones.zone_structures.zone_structure^1",
            "mineralization.alteration_intensity^1",
            "point_location_description^1",
            // "production.commodities.commodity_mass^1",
            "production.commodities.unit_of_measure^1",
            "production.source^1",
            // "production.tonnes^1",
            "references.location^1",
            "references.page_number^1",
            // "references.release_date^1",
            "references.type^1",
            "references.url^1",
            "related_deposit_flag^1",
            "reserves.id^1",
            // "reserves.tonnes^1",
            "scale.name^1",
            // "site_visits.visit_date^1",
            "scale^1",
            "format^1",
            "go_file.url^1",
            "go_file.dir^1",
            "meta.author^1",
            "file.filename^1",
            // "date_lastmodified^0",
            // "features.date_lastmodified^0",
            "features.latitude_decimal_degrees^0",
            "features.longitude_decimal_degrees^0",
            "features.utm_easting^0",
            "features.utm_northing^0",
            "features.utm_zone^0",
            "latitude_decimal_degrees^0",
            "longitude_decimal_degrees^0",
            "utm_easting^0",
            "utm_northing^0",
            "utm_zone^0",
            "contact_rgp.contact^0",
            "contact_rgp.rgp_district^0",
            "drill_hole_count^0",
            // "last_updated_at^0",
            "last_updated_detail^0",
            "location.lat_dec^0",
            "location.ll_datum^0",
            "location.lng_dec^0",
            "location.utm_datum^0",
            "location.utm_easting^0",
            "location.utm_northing^0",
            "location.utm_zone^0",
            // "maps.created_at^0",
            // "maps.created_by^0",
            "maps.document_type_id^0",
            // "maps.file_size^0",
            // "maps.id^0",
            "maps.nr_items^0",
            "maps.subject_id^0",
            "maps.target_path^0",
            "maps.tech_id^0",
            // "maps.updated_at^0",
            // "maps.updated_by^0",
            // "technical_reports.file_size^0",
            // "technical_reports.pages^0",
            "contact_rgp.contact^0",
            "contact_rgp.rgp_district^0",
            "lat_dec^0",
            "length_unit^0",
            "ll_datum^0",
            "lng_dec^0",
            // "all_names.ranking^0",
            // "characteristics.rank^0",
            // "classifications.classification_ranking^0",
            // "comments.created_at^0",
            "contact_rgp.contact^0",
            "contact_rgp.rgp_district^0",
            // "create_date^0",
            "deposit_name^0",
            // "lithology.rank^0",
            // "mineral_zones.ranking^0",
            // "mineralization.alteration_ranking^0",
            // "mineralization.economic_ranking^0",
            // "primary_commodities.ranking^0",
            "production.id^0",
            "production.total_flag^0",
            "references.ogs_publ^0",
            // "secondary_commodities.ranking^0",
            // "updated_at^0",
            "updated_section^0",
            // "contributors.seniority^0",
            // "price^0",
            // "topics.sequence^0",
            "go_file.mime_type^0",
            // "go_file.file_size^0",
            "go_file.limited_distribution^0",
            // "go_file.updated_at^0",
            "content^1"
          ]
        }
      });
    }

    // Ensures a match to at least one of the queries
    if (this.hasSearchTerm()) {
      this.minimumShouldMatch += 1;
    }

    if (this.enableFilter) {
      this.applyFilters(elasticSearchQueryBody);
    }
  }

  addAssessmentDateFilter(): any {
    return {
      bool: {
        should: [
          {
            bool: {
              must: [
                {
                  exists: {
                    field: "year_from",
                  }
                },
                {
                  exists: {
                    field: "year_to",
                  }
                },
                {
                  range: {
                    year_from: {
                      gte:
                        this.yearFrom.value && this.yearFrom.value !== ""
                          ? this.yearFrom.value
                          : null
                    }
                  }
                },
                {
                  range: {
                    year_to: {
                      gt: 0,
                      lte:
                        this.yearTo.value && this.yearTo.value !== ""
                          ? this.yearTo.value
                          : null
                    }
                  }
                }
              ]
            }
          }
        ]
      }
    };
  }

  addAbandonedMineDateFilter(): any {
    return {
      bool: {
        should: [
          {
            bool: {
              must: [
                {
                  exists: {
                    field: "date_mineopened",
                  }
                },
                {
                  exists: {
                    field: "date_mineclosed",
                  }
                },
                {
                  range: {
                    date_mineopened: {
                      gte:
                        this.yearFrom.value && this.yearFrom.value !== ""
                          ? this.yearFrom.value
                          : null
                    }
                  }
                },
                {
                  range: {
                    date_mineclosed: {
                      lte:
                        this.yearTo.value && this.yearTo.value !== ""
                          ? this.yearTo.value
                          : null
                    }
                  }
                }
              ]
            }
          }
        ]
      }
    };
  }

  // Used by Drill Hole and Publication Date Filter
  addDateFilter(fieldName: string): any {
    if (
      this.yearFrom.value &&
      this.yearFrom.value !== "" &&
      this.yearTo.value &&
      this.yearTo.value !== ""
    ) {
      return {
        bool: {
          should: [
            {
              bool: {
                must: [
                  {
                    exists: {
                      field: fieldName,
                    }
                  },
                  {
                    range: {
                      [fieldName]: {
                        gte:
                          this.yearFrom.value && this.yearFrom.value !== ""
                            ? this.yearFrom.value
                            : null,
                        lte:
                          this.yearTo.value && this.yearTo.value !== ""
                            ? this.yearTo.value
                            : null,
                      }
                    }
                  }
                ]
              }
            }
          ]
        }
      };
    } else if (this.yearFrom.value && this.yearFrom.value !== "") {
      return {
        bool: {
          should: [
            {
              bool: {
                must: [
                  {
                    exists: {
                      field: fieldName,
                    },
                  },
                  {
                    range: {
                      [fieldName]: {
                        gte:
                          this.yearFrom.value && this.yearFrom.value !== ""
                            ? this.yearFrom.value
                            : null,
                      },
                    },
                  },
                  //   range: {
                  //     [fieldName]: {
                  //       gte:
                  //         this.yearFrom.value && this.yearFrom.value !== ""
                  //           ? this.yearFrom.value
                  //           : null,
                  //     }
                  //   }
                  // },
                  // {
                  //   range: {
                  //     [fieldName]: {
                  //       gt: null
                  //     }
                  //   }
                  // }
                ],
              },
            },
          ],
        },
      };
    } else {
      return {
        bool: {
          should: [
            {
              bool: {
                must: [
                  {
                    exists: {
                      field: fieldName,
                    },
                  },
                  {
                    range: {
                      [fieldName]: {
                        lte:
                          this.yearTo.value && this.yearTo.value !== ""
                            ? this.yearTo.value
                            : null,
                      },
                    },
                  },
                  // {
                  //   range: {
                  //     [fieldName]: {
                  //       lte:
                  //         this.yearTo.value && this.yearTo.value !== ""
                  //           ? this.yearTo.value
                  //           : null
                  //     }
                  //   }
                  // },
                  // {
                  //   range: {
                  //     [fieldName]: {
                  //       gt: null
                  //     }
                  //   }
                  // }
                ],
              },
            },
          ],
        },
      };
    }
  }

  // Used by NTS, Township and Commodity to add filters against the query
  addFilter(fieldName: string, list: any[], arr: any[]): any {
    for (var i = 0; i < list.length; i++) {
      arr.push({ match_phrase: { [fieldName]: list[i] } });
    }
  }

  applyFilters(elasticSearchQueryBody: ElasticSearchQueryBody) {
    var shouldArr = elasticSearchQueryBody.query.bool.should;

    // Author filter - Publication specific
    if (this.author.value.length > 0) {
      this.minimumShouldMatch += 1;
      for (var i = 0; i < this.author.value.length; i++) {
        shouldArr.push({
          match_phrase: { "contributors.contributor": this.author.value[i] }
        });
      }
    }

    // Series filter - Publication specific
    if (this.series.value.length > 0) {
      this.minimumShouldMatch += 1;
      for (var i = 0; i < this.series.value.length; i++) {
        shouldArr.push({
          match_phrase: { series_en: this.series.value[i]._source.name_en }
        });
      }
    }

    // Year filter
    if (
      (this.yearFrom.value && this.yearFrom.value !== "") ||
      (this.yearTo.value && this.yearTo.value !== "")
    ) {
      this.dateFilterEnabled = true;
      this.minimumShouldMatch += 1;

      if (this.assessmentSelected) {
        elasticSearchQueryBody.query.bool.should.push(
          this.addAssessmentDateFilter()
        );
      }

      if (this.drillHolesSelected) {
        elasticSearchQueryBody.query.bool.should.push(
          this.addDateFilter("year_drilled")
        );
      }

      if (this.publicationsSelected) {
        elasticSearchQueryBody.query.bool.should.push(
          this.addDateFilter("year")
        );
      }

      if(this.abandonedMinesSelected) {
        elasticSearchQueryBody.query.bool.should.push(
          this.addAbandonedMineDateFilter()
        );
      }
    }

    // NTS filter
    if (this.nts.value.length > 0) {
      this.minimumShouldMatch += 1;
      var ntsArr = [];
      var ntsList = this.nts.value;

      if (this.publicationsSelected) {
        this.addFilter("nts.area", ntsList, ntsArr);
      }

      if (this.drillHolesSelected) {
        this.addFilter("nts_num", ntsList, ntsArr);
      }

      if (this.assessmentSelected) {
        this.addFilter("nts.nts_num", ntsList, ntsArr);
      }

      if (this.mineralInventorySelected) {
        this.addFilter("nts.nts_quadrangle", ntsList, ntsArr);
      }

      if(this.abandonedMinesSelected) {
        this.addFilter("nts_number", ntsList, ntsArr);
      }

      shouldArr.push({ bool: { minimum_should_match: 1, should: ntsArr } });
    }

    // Township filter
    if (this.township.value.length > 0) {
      this.minimumShouldMatch += 1;
      var townshipArr = [];
      var townships = this.township.value;

      if (this.publicationsSelected) {
        this.addFilter("townships.name", townships, townshipArr);
      }

      if (this.drillHolesSelected) {
        this.addFilter("township", townships, townshipArr);
      }

      if (this.assessmentSelected) {
        this.addFilter("primary_township", townships, townshipArr);
        this.addFilter("secondary_townships.name", townships, townshipArr);
      }

      if (this.mineralInventorySelected) {
        this.addFilter("townships.name", townships, townshipArr);
      }

      if(this.abandonedMinesSelected) {
        this.addFilter("mndm_township_area_name", townships, townshipArr);
      }

      shouldArr.push({
        bool: { minimum_should_match: 1, should: townshipArr }
      });
    }

    // Commodity filter
    if (this.commodity.value.length > 0) {
      this.minimumShouldMatch += 1;
      var commodityArr = [];
      var commodities = this.commodity.value;
      var sanitizedCommodities = [];

      //Commodity fix for #92893
      for(var i = 0; i < commodities.length; i++) {
        var commodity = commodities[i];
        const regex = new RegExp(`[${this.specialCharacters.map(ch => '\\' + ch).join('')}]`, 'g');
        sanitizedCommodities.push(commodity.replace(regex, ' '));
      }

      if (this.drillHolesSelected) {
        this.addFilter("elements.name", sanitizedCommodities, commodityArr);
      }

      if (this.assessmentSelected) {
        this.addFilter("commodities.description", sanitizedCommodities, commodityArr);
      }

      if (this.mineralInventorySelected) {
        this.addFilter(
          "primary_commodities.description",
          sanitizedCommodities,
          commodityArr
        );
        this.addFilter(
          "secondary_commodities.description",
          sanitizedCommodities,
          commodityArr
        );
      }

      if(this.abandonedMinesSelected) {
        this.addFilter("primary_commodity", sanitizedCommodities, commodityArr);
      }

      shouldArr.push({
        bool: { minimum_should_match: 1, should: commodityArr }
      });
    }
  }

  // formatSearchInput(searchTerm: string): string {
  //   var wildcard = this.autoCompleteOptionSelected ? "" : "*";
  //   var formattedSearchTerm = wildcard + searchTerm + wildcard;
  //   this.searchTerms = formattedSearchTerm;
  //   return formattedSearchTerm;
  // }

  fetchIndexes(): string {
    var indexArray = [];
    for (var i = 0; i < this.databases.value.length; i++) {
      var database = this.databases.value[i];
      if (database.checked) {
        indexArray = indexArray.concat(database.index);
      }
    }

    var result: string;
    for (var i = 0; i < this.resultTypes.value.length; i++) {
      var resultType = this.resultTypes.value[i];
      if (resultType.checked) {
        result = resultType.name;
        break;
      }
    }

    switch (result) {
      case "Record Index":
        indexArray = indexArray.filter((index) =>
          Constants.RECORDS.includes(index)
        );
        break;
      case "Document Contents":
        indexArray = indexArray.filter((index) =>
          Constants.DOCUMENTS.includes(index)
        );
        break;
      default:
        break;
    }

    // Do not include Mineral Inventory if date filters are applied
    if (
      this.dateFilterEnabled &&
      indexArray.indexOf("mineral-inventory") >= 0
    ) {
      indexArray.splice(indexArray.indexOf("mineral-inventory"), 1);
    }

    // Do not include Publication if commodity filters are applied
    if (this.commodityFilterEnabled && indexArray.indexOf("publication") >= 0) {
      indexArray.splice(indexArray.indexOf("publication"), 1);
    }

    var indexes = "";
    if (indexArray) {
      if (indexArray.length == 1) {
        indexes = indexArray[0];
      } else if (indexArray.length > 1) {
        for (var i = 0; i < indexArray.length; i++) {
          var index = indexArray[i];
          indexes += index;
          if (i !== indexArray.length - 1) {
            indexes += ",";
          }
        }
      }
    }
    return indexes;
  }

  completionSuggest(event: any) {
    // User Story #48480
    if(this.searchValue.value.length > 2) {
      var assessmentBody = {
        _source: "suggest",
        suggest: {
          workTypesSuggest: {
            prefix: this.searchValue.value,
            completion: {
              field: "work_types.description.workTypesSuggest",
              skip_duplicates: true,
              size: 3
            }
          },
          performedForSuggest: {
            prefix: this.searchValue.value,
            completion: {
              field: "performed_for.name.performedForSuggest",
              skip_duplicates: true,
              size: 3
            }
          }
        }
      };
  
      var drillHoleBody = {
        _source: "suggest",
        suggest: {
          "companyNameSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "company_name.companyNameSuggest",
              skip_duplicates: true,
              size: 3
            }
          }
        }
      };

      var mineralInventoryBody = {
        _source: "suggest",
        suggest: {
          "mdNameSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "all_names.md_name.mdNameSuggest",
              skip_duplicates: true,
              size: 3
            }
          },
          "mineralNameSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "mineralization.mineral_name.name.mineralNameSuggest",
              skip_duplicates: true,
              size: 3
            }
          }
        }
      };

      var publicationBody = {
        _source: "suggest",
        suggest: {
          "contributorSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "contributors.contributor.companyNameSuggest",
              skip_duplicates: true,
              size: 3
            }
          },
          "titleSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "title.titleSuggest",
              skip_duplicates: true,
              size: 3
            }
          }
        }
      };

      var abandonedMinesBody = {
        _source: "suggest",
        suggest: {
          "officialNameSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "official_name.officialNameSuggest",
              skip_duplicates: true,
              size: 3
            }
          },
          "mineFeatureTypeSuggest": {
            prefix: this.searchValue.value,
            completion: {
              field: "features.feature_type.mineFeatureTypeSuggest",
              skip_duplicates: true,
              size: 3
            }
          }
        }
      };
  
      var indexSuggestions: any[] = [];
      indexSuggestions[DatabaseIndexNameType.assessment] = assessmentBody;
      indexSuggestions[DatabaseIndexNameType.drillHoles] = drillHoleBody;
      indexSuggestions[DatabaseIndexNameType.mineralInventory] = mineralInventoryBody;
      indexSuggestions[DatabaseIndexNameType.publication] = publicationBody;
      indexSuggestions[DatabaseIndexNameType.abandonedMines] = abandonedMinesBody;
  
      const input = String.fromCharCode(event.keyCode);
      if (/[a-zA-Z0-9]/.test(input) || (event.keyCode == 8 || event.keyCode == 46)) {
        this.autoCompleteSuggestions = [];
        var suggestionQueryUrls: Observable<any>[] = [];
  
        var indexes = this.indexes.split(',');
        for(var i = 0; i < indexes.length; i++) {
          if(indexSuggestions[indexes[i]]) {
            suggestionQueryUrls.push(this.elasticSearchQueryService.getIndexCompletionSuggestions(indexes[i], indexSuggestions[indexes[i]]));
          }
        }
  
        forkJoin(suggestionQueryUrls).pipe().subscribe(res => {
          for(var i = 0; i < res.length; i++) {
            var suggest = res[i].suggest;
            var suggestionKeys = Object.keys(suggest);
            for(var j = 0; j < suggestionKeys.length; j++) {
              var suggestions = suggest[suggestionKeys[j]][0].options;
              if(suggestions.length > 0) {
                this.autoCompleteSuggestions = this.autoCompleteSuggestions.concat(suggestions);
              }
            }
          }

          this.autoCompleteSuggestions = this.autoCompleteSuggestions.filter(
            (v, i, a) => a.findIndex(t => t.text === v.text || t.text === v.text) === i
          );

        });
      }
    } else {
      this.autoCompleteSuggestions = [];
    }
  }

  fetchData() {
    this.searchPressed = true;

    var elasticSearchQueryBody: ElasticSearchQueryBody = {
      from: this.startIndex,
      size: this.resultsPerPage,
      sort: [{ _score: { order: "desc" } }]
    };

    this.constructESQueryStructure(elasticSearchQueryBody);

    var hasIndexes: boolean = this.indexes != "" ? true : false;
    elasticSearchQueryBody.query.bool["minimum_should_match"] = this.minimumShouldMatch;

    if (hasIndexes) {
      var postRequest : any = {
        indexes: this.indexes,
        body: JSON.stringify(elasticSearchQueryBody)
      }

      this.http
        .post(environment.apim_url, postRequest, this.httpOptions)
        .subscribe((res: any) => {
          this.results = res.hits.hits;
          this.cd();

          this.searchResultsCache.setTotalResults(res.hits.total.value);
          this.totalResults = this.searchResultsCache.getTotalResults();

          this.endIndex = this.startIndex + this.resultsPerPage;
          if (this.endIndex > this.totalResults) {
            this.endIndex = this.totalResults;
          }

          this.searchResultsCache.setLastSearchResult(
            this.results[this.results.length - 1]
          );

          this.displayPagination = true;
          this.searchResultsCache.setResults(this.results);
          this.searchResultsCache.setForm(this.f);
          this.updatePaginationOptions(
            this.startIndex,
            this.endIndex,
            this.resultsPerPage,
            this.pageIndex
          );

          if (!this.autoCompleteOptionSelected && this.hasSearchTerm()) {
            this.elasticSearchQueryService
              .getCorrectionSuggestions(this.searchValue.value)
              .subscribe((c: any) => {
                this.corrections = c.suggest.simple_phrase[0].options;
              });
          }

          this.autoCompleteOptionSelected = false;
        });
    }
  }
  //ElasticSearch Functions

  // Helper Functions
  hasSearchTerm(): boolean {
    return this.searchValue.value &&
      this.searchValue.value.match(/^ *$/) === null
      ? true
      : false;
  }

  resetFilterInputs() {
    this.tempAuthor.setValue("");
    this.tempCommodity.setValue("");
    this.tempNts.setValue("");
    this.tempSeries.setValue("");
    this.tempTownship.setValue("");
    this.dateFilterEnabled = false;
    this.commodityFilterEnabled = false;
  }
  // Helper Functions

  // Button Functions
  onClearPressed() {
    this.autoCompleteSuggestions = [];
    this.searchValue.patchValue("");
  }

  onClearFiltersPressed() {
    var previousSearchTerm = this.searchValue.value;
    this.clearFilter = true;
    this.f = new Search().toForm();
    this.searchValue.setValue(previousSearchTerm);
    this.searchInput = this.searchValue.value;
    this.searchResultsCache.setForm(this.f);
  }

  updateDatabaseBoolean(value: boolean) {
    this.databaseIsSelected = value;
    this.indexes = this.fetchIndexes();
  }

  toggleSearchFilters(value: boolean) {
    this.enableFilter = value;
    this.indexes = this.fetchIndexes();
  }

  correctionSuggestionSelected(newPhrase: string) {
    this.autoCompleteOptionSelected = true;
    this.autoCompleteSuggestions = [];
    this.corrections = [];
    this.searchValue.setValue(newPhrase);
    this.searchInput = newPhrase;
    this.fetchData();
  }

  search() {
    this.searchInput = this.searchValue.value;
    this.initializeParameters();
    this.fetchData();
    this.resetFilterInputs();
  }
  // Button Functions

  // Pagination Functions
  onPageChange(event: PageEvent) {
    this.startIndex = event.pageIndex * event.pageSize;
    this.resultsPerPage = event.pageSize;
    this.pageIndex = event.pageIndex;
    var cachedPageIndex = this.searchResultsCache.getPageIndex();

    this.isViewingLastPage();

    // Used to navigate backwards with ElasticSearch 'search_after'
    this.visitedResults.push(this.results[this.results.length - 1]);
    this.searchResultsCache.setVisitedResults(this.visitedResults);
    var navigatingForward: boolean = this.pageIndex > cachedPageIndex ? true : false;

    if (cachedPageIndex == 0 && this.viewingLastPage) {
      this.searchResultsCache.setVisitedLastPageFirst(true);
      this.visitedLastPageFirst = true;
    }

    this.updatePaginationOptions(
      this.startIndex,
      this.endIndex,
      this.resultsPerPage,
      this.pageIndex
    );

    if (this.visitedLastPageFirst || !navigatingForward) {
      /*
        IF THE LAST PAGE WAS VIEWED FIRST, THE PREVIOUS PAGE'S RESULTS WILL NOT HAVE BEEN DISPLAYED
        WE MUST QUERY FOR THE PREVIOUS RESULTS IN THE EVENT THAT THE USER WANTS TO NAVIGATE BACKWARDS
      */

      if (!this.viewingLastPage) {
        // Normal pagination
        this.fetchData();
      } else {
        this.fetchLastPage();
      }
        
    } else if (this.viewingLastPage) {
      this.fetchLastPage();
    } else {
      // Normal pagination
      this.fetchData();
    }

    const searchResultsTotal = document.getElementById("searchResultsTotal");
    let position = searchResultsTotal.getBoundingClientRect();
    window.scrollTo({
      top: position.top + window.scrollY - 20,
      left: position.left,
      behavior: "smooth"
    });
  }

  isViewingLastPage() {
    this.viewingLastPage =
      this.totalResults > 0 &&
      this.pageIndex * this.resultsPerPage + this.resultsPerPage >=
        this.totalResults
        ? true
        : false;
  }

  fetchLastPage() {
    //ONLY FETCH THE LAST PAGE OF RESULTS IF THE 'Last Page' BUTTON IS PRESSED
    var from = this.resultsPerPage * this.pageIndex;

    var elasticSearchQueryBody: ElasticSearchQueryBody = {
      from: from,
      size: (this.totalResults - from) >= this.resultsPerPage ? this.resultsPerPage : (this.totalResults - from),
      sort: [{ _score: { order: "desc" } }]
    };

    this.searchResultsCache.setStartIndex(from);
    this.constructESQueryStructure(elasticSearchQueryBody);

    //Fix for #92893 - incorrect minimum_should_match when paginating
    elasticSearchQueryBody.query.bool["minimum_should_match"] = this.minimumShouldMatch;

    var postRequest : any = {
      indexes: this.indexes,
      body: JSON.stringify(elasticSearchQueryBody)
    }
    
    this.http
      .post(environment.apim_url, postRequest, this.httpOptions)
      .subscribe((res: any) => {
        this.results = res.hits.hits;
        this.searchResultsCache.setEndIndex(this.results.length);
        this.searchResultsCache.setResults(this.results);
      });

    this.endIndex = this.totalResults;
  }

  updatePaginationOptions(
    startIndex: number,
    endIndex: number,
    resultsPerPage: number,
    pageIndex: number
  ) {
    this.searchResultsCache.setStartIndex(startIndex);
    this.searchResultsCache.setEndIndex(endIndex);
    this.searchResultsCache.setResultsPerPage(resultsPerPage);
    this.searchResultsCache.setPageIndex(pageIndex);
  }
  // Pagination Functions

  // Loading Spinner Functions
  private connectPageListeners() {
    this.onLoadingStatusChange();
  }

  private onLoadingStatusChange() {
    this.loadingService.loading$
      .pipe(takeWhile(() => this.alive))
      .subscribe((loading: boolean) => {
        this.displayProgressSpinner = loading;
        this.spinnerWithoutBackdrop = loading;
        this.changeDetectorRef.markForCheck();
      });
  }
  // Loading Spinner Functions

  // Export Spreadsheet Functions
  exportToExcel() {
    this.mineral_inventory_arr = [];
    this.assessment_arr = [];
    this.drill_hole_arr = [];
    this.publication_arr = [];
    this.abandoned_mine_arr = [];
    this.assessment_pdf_arr = [];
    this.publication_pdf_arr = [];

    for (var i = 0; i < this.results.length; i++) {
      var index = this.results[i]._index;
      var record = this.results[i]._source;

      switch (index) {
        case DatabaseIndexNameType.mineralInventory:
          var mineralObj = {
            "OMI Number": record.mdi_ident,
            Status: record.deposit_status,
            "Primary Commodity": this.commaSeparatedPipe.transform(
              record.primary_commodities,
              "description"
            ),
            "Township or area": this.commaSeparatedPipe.transform(
              record.townships,
              "name"
            )
          };
          this.mineral_inventory_arr.push(mineralObj);
          break;
        case DatabaseIndexNameType.assessment:
          var year = "";
          if (record.year_from != 0 && record.year_to != 0) {
            if (record.year_from == record.year_to) {
              year = record.year_from + "";
            } else {
              year = record.year_from + "-" + record.year_to;
            }
          }

          var assessmentObj = {
            "Report Number": record.file_id,
            Year: year,
            "Performed For": this.commaSeparatedPipe.transform(
              record.performed_for,
              "name"
            ),
            "Work Type": this.commaSeparatedPipe.transform(
              record.work_types,
              "description"
            )
          };
          this.assessment_arr.push(assessmentObj);
          break;
        case DatabaseIndexNameType.drillHoles:
          var drillObj = {
            "Report Number": record.tech_id,
            Company: record.company_name,
            "Township or area": record.township,
            Year: record.year_drilled
          };
          this.drill_hole_arr.push(drillObj);
          break;
        case DatabaseIndexNameType.publication:
          var publicationObj = {
            Publication: record.publ_num,
            "Title": record.title,
            Author: this.commaSeparatedPipe.transform(
              record.contributors,
              "contributor"
            ),
            "Year Published": record.year,
            "Township or area": this.commaSeparatedPipe.transform(
              record.townships,
              "name"
            ),
            NTS: this.commaSeparatedPipe.transform(record.nts, "area")
          };
          this.publication_arr.push(publicationObj);
          break;
        case DatabaseIndexNameType.assessment_pdf:
          var assessmentPdfObj = {
            "File Name": record.file.filename,
            "Title": record.meta.title,
            "Author Name": record.meta.author,
            "File Size (MB)": this.megabyteConversionPipe.transform(
              record.file.filesize
            )
          };
          this.assessment_pdf_arr.push(assessmentPdfObj);
          break;
        case DatabaseIndexNameType.publication_pdf:
          var publicationPdfObj = {
            "File Name": record.file.filename,
            "Title": record.meta.title,
            "Author Name": record.meta.author,
            "File Size (KB)": this.megabyteConversionPipe.transform(
              record.file.filesize
            )
          };
          this.publication_pdf_arr.push(publicationPdfObj);
          break;
        default:
          var abandonedMineObj = {
            "Abandoned Mine": record.abandoned_mine_identifier,
            "Mine site name": record.official_name,
            "Township": record.mndm_township_area_name,
            "Commodity": record.primary_commodity
          };
          this.abandoned_mine_arr.push(abandonedMineObj);
          break;
      }
    }

    const workbook = XLSX.utils.book_new();

    if (this.mineral_inventory_arr.length > 0) {
      const mineralInventoryWorksheet: XLSX.WorkSheet =
        XLSX.utils.json_to_sheet(this.mineral_inventory_arr);
      this.addExcelHyperlinks(
        this.mineral_inventory_arr,
        mineralInventoryWorksheet,
        "OMI Number",
        DatabaseIndexNameType.mineralInventory,
        null
      );
      XLSX.utils.book_append_sheet(
        workbook,
        mineralInventoryWorksheet,
        "MineralInventory"
      );
      mineralInventoryWorksheet["!cols"] = [
        { wch: 20 },
        { wch: 55 },
        { wch: 40 },
        { wch: 30 }
      ];
    }

    if (this.assessment_arr.length > 0) {
      const assessmentWorksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
        this.assessment_arr
      );
      this.addExcelHyperlinks(
        this.assessment_arr,
        assessmentWorksheet,
        "Report Number",
        DatabaseIndexNameType.assessment,
        null
      );
      XLSX.utils.book_append_sheet(
        workbook,
        assessmentWorksheet,
        "AssessmentFiles"
      );
      assessmentWorksheet["!cols"] = [
        { wch: 12 },
        { wch: 10 },
        { wch: 50 },
        { wch: 150 }
      ];
    }

    if (this.drill_hole_arr.length > 0) {
      const drillHolesWorksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
        this.drill_hole_arr
      );
      this.addExcelHyperlinks(
        this.drill_hole_arr,
        drillHolesWorksheet,
        "Report Number",
        DatabaseIndexNameType.drillHoles,
        null
      );
      XLSX.utils.book_append_sheet(workbook, drillHolesWorksheet, "DrillHoles");
      drillHolesWorksheet["!cols"] = [
        { wch: 12 },
        { wch: 50 },
        { wch: 20 },
        { wch: 10 },
      ];
    }

    if (this.publication_arr.length > 0) {
      const publicationWorksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
        this.publication_arr
      );
      this.addExcelHyperlinks(
        this.publication_arr,
        publicationWorksheet,
        "Publication",
        DatabaseIndexNameType.publication,
        null
      );
      XLSX.utils.book_append_sheet(
        workbook,
        publicationWorksheet,
        "Publications"
      );
      publicationWorksheet["!cols"] = [
        { wch: 12 },
        { wch: 115 },
        { wch: 40 },
        { wch: 15 },
        { wch: 80 },
        { wch: 30 },
      ];
    }

    if (this.assessment_pdf_arr.length > 0) {
      const assessmentPdfWorksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
        this.assessment_pdf_arr
      );
      this.addExcelHyperlinks(
        this.assessment_pdf_arr,
        assessmentPdfWorksheet,
        "File Name",
        DatabaseIndexNameType.assessment_pdf,
        record.path.virtual
      );
      XLSX.utils.book_append_sheet(
        workbook,
        assessmentPdfWorksheet,
        "AssessmentDocuments"
      );
      assessmentPdfWorksheet["!cols"] = [
        { wch: 20 },
        { wch: 30 },
        { wch: 30 },
        { wch: 10 }
      ];
    }

    if (this.publication_pdf_arr.length > 0) {
      const publicationPdfWorksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
        this.publication_pdf_arr
      );
      this.addExcelHyperlinks(
        this.publication_pdf_arr,
        publicationPdfWorksheet,
        "File Name",
        DatabaseIndexNameType.publication_pdf,
        record.path.virtual
      );
      XLSX.utils.book_append_sheet(
        workbook,
        publicationPdfWorksheet,
        "PublicationDocuments"
      );
      publicationPdfWorksheet["!cols"] = [
        { wch: 20 },
        { wch: 30 },
        { wch: 30 },
        { wch: 10 }
      ];
    }

    if (this.abandoned_mine_arr.length > 0) {
      const abandonedMinesWorksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
        this.abandoned_mine_arr
      );
      this.addExcelHyperlinks(
        this.abandoned_mine_arr,
        abandonedMinesWorksheet,
        "Abandoned Mine",
        DatabaseIndexNameType.abandonedMines,
        null
      );
      XLSX.utils.book_append_sheet(
        workbook,
        abandonedMinesWorksheet,
        "AbandonedMines"
      );
      abandonedMinesWorksheet["!cols"] = [
        { wch: 15 },
        { wch: 40 },
        { wch: 40 },
        { wch: 40 }
      ];
    }

    this.saveAsExcelFile(XLSX.write(workbook, { bookType: "xlsx", type: "array" }), "GeologyOntarioSearchResults");
  }

  addExcelHyperlinks(
    arr: any[],
    ws: XLSX.WorkSheet,
    cellName: string,
    index: string,
    pdfPath: string
  ) {
    for (let i = 0; i < arr.length; i++) {
      var fileNumber = arr[i][cellName];
      var target = "";

      if(index == DatabaseIndexNameType.assessment_pdf) {
        target = environment.azure_blob_storage + DatabaseIndexNameType.assessment + pdfPath;
      } else if(index == DatabaseIndexNameType.publication_pdf) {
        target = environment.azure_blob_storage + DatabaseIndexNameType.publication + pdfPath;
      } else {
        target = environment.application_url + "persistent-linking?" + index + "=" + fileNumber;
      }

      ws[
        XLSX.utils.encode_cell({
          c: 0,
          r: i + 1
        })
      ].l = {
        Target:
          target
      };
    }
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
    });
    FileSaver.saveAs(data, fileName + "-" + (moment(new Date())).format('MMM-DD-YYYY-HH-mm-ss') + ".xlsx");
  }
  // Export Spreadsheet Functions

  get form() {
    return this.f?.controls;
  }

  get searchValue() {
    return this.f.get("searchValue");
  }

  get resultTypes() {
    return this.f.get("resultTypes");
  }

  get databases() {
    return this.f.get("databases");
  }

  get yearFrom() {
    return this.f.get("yearFrom");
  }

  get yearTo() {
    return this.f.get("yearTo");
  }

  get series() {
    return this.f.get("series");
  }

  get tempSeries() {
    return this.f.get("tempSeries");
  }

  get author() {
    return this.f.get("author");
  }

  get tempAuthor() {
    return this.f.get("tempAuthor");
  }

  get nts() {
    return this.f.get("nts");
  }

  get tempNts() {
    return this.f.get("tempNts");
  }

  get commodity() {
    return this.f.get("commodity");
  }

  get tempCommodity() {
    return this.f.get("tempCommodity");
  }

  get township() {
    return this.f.get("township");
  }

  get tempTownship() {
    return this.f.get("tempTownship");
  }

  get abandonedMinesSelected(): boolean {
    return this.databases.value[DatabaseType.abandonedMines].checked;
  }

  get assessmentSelected(): boolean {
    return this.databases.value[DatabaseType.assessment].checked;
  }

  get drillHolesSelected(): boolean {
    return this.databases.value[DatabaseType.drillHoles].checked;
  }

  get mineralInventorySelected(): boolean {
    return this.databases.value[DatabaseType.mineralInventory].checked;
  }

  get publicationsSelected(): boolean {
    return this.databases.value[DatabaseType.publications].checked;
  }

  get allDatabasesSelected(): boolean {
    return this.databases.value[DatabaseType.allDatabases].checked;
  }
}